import React, { useState, useRef, useEffect } from 'react'

interface UseDropdownReturn {
  isOpen: boolean
  containerRef: React.RefObject<HTMLDivElement>
  buttonRef: React.RefObject<HTMLButtonElement>
  itemRefs: React.MutableRefObject<Array<HTMLLIElement | null>>
  setItemRef: (index: number) => (el: HTMLLIElement | null) => void
  handleToggle: () => void
  handleButtonKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  handleItemKeyDown: (e: React.KeyboardEvent<HTMLLIElement>, index: number) => void
  close: () => void
}

const useDropdown = (): UseDropdownReturn => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<Array<HTMLLIElement | null>>([])
  const pendingFocusRef = useRef<number | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (containerRef.current != null && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && pendingFocusRef.current !== null) {
      const index = pendingFocusRef.current
      pendingFocusRef.current = null
      itemRefs.current[index]?.focus()
    }
  }, [isOpen])

  const handleToggle = (): void => {
    setIsOpen(prev => {
      if (!prev) itemRefs.current = []
      return !prev
    })
  }

  const close = (): void => {
    setIsOpen(false)
  }

  const closeAndFocusButton = (): void => {
    setIsOpen(false)
    buttonRef.current?.focus()
  }

  const handleButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!isOpen) {
        setIsOpen(true)
        pendingFocusRef.current = 0
      } else {
        itemRefs.current[0]?.focus()
      }
    }
  }

  const handleItemKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, index: number): void => {
    const totalItems = itemRefs.current.length
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (index < totalItems - 1) itemRefs.current[index + 1]?.focus()
        break
      case 'ArrowUp':
        e.preventDefault()
        if (index > 0) itemRefs.current[index - 1]?.focus()
        else closeAndFocusButton()
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        itemRefs.current[index]?.click()
        requestAnimationFrame(() => {
          buttonRef.current?.focus()
        })
        break
      case 'Escape':
        e.preventDefault()
        closeAndFocusButton()
        break
    }
  }

  const setItemRef = (index: number) => (el: HTMLLIElement | null): void => {
    itemRefs.current[index] = el
  }

  return {
    isOpen,
    containerRef,
    buttonRef,
    itemRefs,
    setItemRef,
    handleToggle,
    handleButtonKeyDown,
    handleItemKeyDown,
    close
  }
}

export { useDropdown }
